import { yupResolver } from "@hookform/resolvers/yup";
import { Delete } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useContext, useEffect, useRef, useState } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { getData } from "../../Services/getData";
import { postData } from "../../Services/postData";
import { updateData } from "../../Services/updateData";
import Config from "../../Utils/Config";
import { FormsIds } from "../../Utils/Constants";
import { InstructorFormValidationSchema } from "../../Utils/Models";
import { InstructorFormProps } from "../../Utils/Types";
import { endpoints } from "../../Utils/UrlEnum";
import { onFormError } from "../../Utils/Utils";
import { Vocabulary } from "../../Utils/Vocabulary";
import { CRUDContext } from "./InstructorsTable";

export default function InstructorsForm(props: InstructorFormProps) {
  // Use the provided vehicle or fallback to the CRUDContext
  const instructorContext: any = useContext(
    props.selectedInstructor ? props.selectedInstructor : CRUDContext
  );
  const [vehicles, setVehicles] = useState([]);
  const formRef = useRef<HTMLFormElement>(null);
  const firstUpdate = useRef(true);

  // Initialize the form with Yup validation and default values
  const { register, control, handleSubmit, reset } = useForm({
    resolver: yupResolver(InstructorFormValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      // Include an empty array for documents (document_ids)
      document_ids: [],
    },
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });

  // Use useFieldArray for the dynamic document rows
  const { fields, append, remove } = useFieldArray({
    control,
    name: "document_ids",
  });

  const formData = useWatch({ control });

  // Update context when form data changes
  useEffect(() => {
    if (formData && !firstUpdate.current) {
      props.setContextValue({ ...formData });
    }
  }, [formData]);

  /**
   * Fetch vehicles
   */
  useEffect(() => {
    const fetchVehicles = async () => {
      const url = `${endpoints.vehicle.main}/paginated?page=0&perPage=100000`;
      const res = (await getData(url)) as any;
      if (!res || res?.error) {
        return;
      }
      setVehicles(res.data.results);
    };
    fetchVehicles();
  }, []);

  // Populate the form with instructorContext values on mount if available
  useEffect(() => {
    if (instructorContext.name && instructorContext.name !== "") {
      reset(instructorContext);
    }
  }, [instructorContext, reset]);

  // Automatically submit the form if needed (based on your original logic)
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    formRef.current?.submit();
  }, []);

  useEffect(() => {
    if (instructorContext && instructorContext._id) {
      reset({
        ...instructorContext,
        user_id: instructorContext._id || null,
        _id: null,
        vehicle_ids: vehicles?.filter((vehicle: any) =>
          instructorContext.instructorDetails[0]?.vehicle_ids?.includes(
            vehicle._id
          )
        ),
        document_ids:
          instructorContext.instructorDetails[0]?.documentsDetails
            ?.filter((doc: any) => {
              // Get all _id values from documentIds (predefined documents)
              const predefinedDocumentIds = instructorContext.documentIds
                ? Object.keys(instructorContext.documentIds)
                    .filter((key) => key !== "_id")
                    .map((key) => instructorContext.documentIds[key]?._id)
                    .filter((id) => id != null)
                : [];
              // Only include documents that are NOT in the predefined list
              return !predefinedDocumentIds.includes(doc._id);
            })
            ?.map((doc: any) => ({
              _id: doc._id || null,
              name: doc.name || "",
              expirationDate: doc.expirationDate || new Date(),
              document: typeof doc.document === "string" ? doc.document : "",
            })) || [],
      });
    }
  }, [instructorContext, reset, vehicles]);

  // Update an existing element
  const updateElement = async (model: any) => {
    const url = `${endpoints.instructor.main}/${instructorContext?.instructorDetails[0]._id}`;
    const formData = new FormData();

    // Create an array for metadata, including file name and extension
    const documentsMeta = model.document_ids.map((doc: any) => {
      let documentName = "";
      let documentExtension = "";
      let _id: string | null = "";
      if (doc.document instanceof File) {
        _id = null;
        documentName = doc.document.name;
        const parts = doc.document.name.split(".");
        documentExtension = parts.length > 1 ? parts.pop() || "" : "";
      } else if (typeof doc.document === "string") {
        _id = doc._id;
        documentName = doc.document; // Existing document URL/path
      }
      return {
        _id: _id,
        name: doc.name,
        expirationDate: doc.expirationDate,
        documentName,
        documentExtension,
      };
    });

    // Append metadata as a JSON string.
    formData.append("document_ids_meta", JSON.stringify(documentsMeta));

    // Append each new file using the key "document_ids".
    model.document_ids.forEach((doc: any) => {
      if (doc.document instanceof File) {
        formData.append("document_ids", doc.document);
      }
    });

    // Append the rest of your model fields.
    Object.keys(model).forEach((key) => {
      if (key == "documentIds") {
        formData.append(key, JSON.stringify(model[key]));
      } else if (key !== "document_ids" && key !== "vehicle_ids") {
        formData.append(key, model[key]);
      } else if (key === "vehicle_ids") {
        const vehicleIds = model[key].map((vehicle: any) => vehicle._id.trim());
        formData.append(key, vehicleIds);
      }
    });

    try {
      const res = (await updateData(url, formData)) as any;
      if (!res || res?.error) {
        console.error("Error response:", res);
        return;
      }
      props.setContextValue([]);
      props.onClose();
    } catch (error) {
      console.error("Error updating vehicle:", error);
    }
  };

  // Create a new element

  const createElement = async (model: any) => {
    const url = `${endpoints.instructor.main}`;
    const formData = new FormData();

    // Create an array for metadata, including file name and extension
    const documentsMeta = model.document_ids?.map((doc: any) => {
      let documentName = "";
      let documentExtension = "";
      if (doc.document instanceof File) {
        documentName = doc.document.name;
        const parts = doc.document.name.split(".");
        documentExtension = parts.length > 1 ? parts.pop() || "" : "";
      }
      return {
        name: doc.name,
        expirationDate: doc.expirationDate,
        documentName,
        documentExtension,
      };
    });

    // Append metadata as a JSON string.
    formData.append("document_ids_meta", JSON.stringify(documentsMeta));

    // Append each file using the key "document_ids".
    model.document_ids?.forEach((doc: any) => {
      if (doc.document instanceof File) {
        formData.append("document_ids", doc.document);
      }
    });

    // Append the rest of your model fields.
    Object.keys(model).forEach((key) => {
      if (key !== "document_ids" && key !== "vehicle_ids") {
        formData.append(key, model[key]);
      } else if (key === "vehicle_ids") {
        const vehicleIds = model[key].map((vehicle: any) => vehicle._id.trim());
        formData.append(key, vehicleIds);
      }
    });

    try {
      const res = (await postData(url, formData)) as any;
      if (!res || res?.error) {
        console.error("Error response:", res);
        return;
      }
      props.onClose();
      props.setContextValue([]);
    } catch (error) {
      console.error("Error creating instructor:", error);
    }
  };

  // Handle form submission
  const onSubmit = (model: any) => {
    instructorContext?.instructorDetails[0]?._id
      ? updateElement(model)
      : createElement(model);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onFormError)}
      method="get"
      ref={formRef}
      id={FormsIds.instructorForm}
    >
      <Grid container spacing={Config.standardGridSpacing}>
        {/* Basic vehicle fields */}
        <Grid item xs={12} sm={12} md={12}>
          <Controller
            defaultValue={[]}
            name="vehicle_ids"
            control={control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Autocomplete
                value={Array.isArray(field?.value) ? field?.value : []}
                freeSolo={false}
                disablePortal
                multiple
                disableClearable
                clearOnBlur={true}
                options={vehicles}
                isOptionEqualToValue={(option: any, value: any) =>
                  option?._id === value?._id
                }
                getOptionLabel={(option: any) =>
                  `${option?.brand} ${option?.model}: ${option?.vehicleNumber}` ||
                  ""
                }
                onChange={(event, value: any) => {
                  field.onChange(value);
                }}
                renderInput={(params) => (
                  <TextField
                    value={field?.value || " "}
                    error={!!error}
                    helperText={error?.message}
                    label={Vocabulary.vehicles}
                    name="vehicle_ids"
                    type="search"
                    {...params}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Controller
                name="canScheduleHours"
                control={control}
                render={({ field: { value, onChange, ref } }) => (
                  <Switch
                    inputRef={ref}
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)} // ✅ ensures boolean
                    color="primary"
                  />
                )}
              />
            }
            label={Vocabulary.canScheduleHours}
          />
        </Grid>
        {/* Document rows */}
        <Grid item xs={12} sm={12} md={12}>
          <Button
            variant="outlined"
            onClick={() =>
              append({
                name: "",
                expirationDate: new Date(),
                document: "",
              })
            }
            style={{ marginTop: "10px", marginBottom: "20px" }}
          >
            {Vocabulary.addDocument}
          </Button>
          <Grid item xs={12} sm={12} md={12}>
            {formData.documentIds &&
              Object.keys(formData.documentIds)?.map((key: any) => {
                if (key == "_id") return null;
                return (
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    key={key}
                    style={{ marginBottom: 20 }}
                  >
                    {/* First Column */}
                    <Grid item xs={12} sm={12} md={6}>
                      <TextField
                        label={
                          Vocabulary.documentName + " (" + Vocabulary[key] + ")"
                        }
                        {...register(
                          `documentIds.${key}.name` as "documentIds.psychological.name"
                        )}
                        fullWidth
                      />
                    </Grid>

                    {/* Second Column */}
                    <Grid item xs={12} sm={12} md={6}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={2}
                        width="100%"
                      >
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <Controller
                            control={control}
                            name={
                              `documentIds.${key}.expirationDate` as "documentIds.psychological.expirationDate"
                            }
                            render={({ field }) => (
                              <DatePicker
                                label={Vocabulary.documentDate}
                                value={
                                  field.value ? new Date(field.value) : null
                                }
                                onChange={(newDate) => field.onChange(newDate)}
                                slots={{ textField: TextField }}
                                format="dd.MM.yyyy"
                                slotProps={{
                                  textField: {
                                    fullWidth: true, // Ensure uniform width
                                  },
                                }}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </Box>
                    </Grid>
                  </Grid>
                );
              })}
          </Grid>
          {fields.map((item, index) => (
            <Grid
              container
              spacing={2}
              alignItems="center"
              key={item.id}
              style={{ marginBottom: 20 }}
            >
              {/* First Column */}
              <Grid item xs={12} sm={12} md={6}>
                <TextField
                  label={Vocabulary.documentName}
                  {...register(`document_ids.${index}.name` as const)}
                  fullWidth
                />
              </Grid>

              {/* Second Column */}
              <Grid item xs={12} sm={12} md={6}>
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Controller
                      control={control}
                      name={`document_ids.${index}.expirationDate` as const}
                      render={({ field }) => (
                        <DatePicker
                          label={Vocabulary.documentDate}
                          value={field.value ? new Date(field.value) : null}
                          onChange={(newDate) => field.onChange(newDate)}
                          slots={{ textField: TextField }}
                          format="dd.MM.yyyy"
                          slotProps={{
                            textField: {
                              fullWidth: true, // Ensure uniform width
                            },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                  <IconButton onClick={() => remove(index)}>
                    <Delete />
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </form>
  );
}
